import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  Briefcase,
  ExternalLink,
  CheckCircle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  FileText,
  Building,
  MapPin,
  Calendar,
  Link as LinkIcon,
  Info,
  Clipboard,
  Sparkles,
  AlertCircle,
  Clock,
  BookmarkPlus,
  Zap,
  X,
  Eye,
  Copy,
  Check,
} from 'lucide-react';

const MODAL_ACCENT: React.CSSProperties = { background: 'var(--brand-cta-bg)' };

import { supabase } from '@soVendor/lib/supabase';
import { OptimizationResult, JobData } from '../shared/types';
import { STEP_MOTION_CLASS, STEP_CARD_COMPACT_CLASS, STEP_WRAPPER_CLASS, STEP_SCROLL_AREA_CLASS, STEP_NAV_CLASS } from '../shared/stepLayout';
import { safeClipboardWrite } from '@soVendor/utils/clipboard';

interface ApplyStepProps {
  onNext: (applicationId?: string) => void;
  onBack: () => void;
  optimizationResult: OptimizationResult;
  jobData: JobData;
  onApply: (data: {
    applicationSnippet: string;
    coverLetterSnippet: string;
    notes?: string;
  }) => Promise<string>;
  isLoading: boolean;
  error: string | null;
  /** When provided, user can exit to dashboard after apply for a smooth end experience */
  onExitToDashboard?: () => void;
  /** When true, user came from Optimization History (Apply from a row); Back is disabled to avoid breaking the flow */
  fromHistoryApply?: boolean;
}

type ApplicationMethod = 'direct' | 'snippets' | 'later' | 'skip' | null;
type ApplicationStatus = 'applied' | 'saved_for_later' | 'in_progress' | 'not_applied';

const ApplyStep: React.FC<ApplyStepProps> = ({
  onNext,
  onBack,
  optimizationResult,
  jobData,
  isLoading,
  onExitToDashboard,
  fromHistoryApply = false
}) => {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const fromHistory = fromHistoryApply || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('history_apply_flow_active') === '1');
  const backDisabled = true;
  const [applicationNotes, setApplicationNotes] = useState('');
  const [coverLetterSnippet, setCoverLetterSnippet] = useState('');
  const [applicationSnippet, setApplicationSnippet] = useState('');
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [generatingApplication, setGeneratingApplication] = useState(false);
  const [generatingCoverLetter, setGeneratingCoverLetter] = useState(false);
  const [savedApplicationId, setSavedApplicationId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [snippetCopied, setSnippetCopied] = useState(false);
  /** Preview modal: null = closed, 'summary' = app summary, 'cover' = cover letter */
  const [previewModal, setPreviewModal] = useState<null | 'summary' | 'cover'>(null);
  const [previewCopied, setPreviewCopied] = useState(false);
  
  // New states for improved tracking
  const [applicationMethod, setApplicationMethod] = useState<ApplicationMethod>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<ApplicationStatus>('saved_for_later');
  const [reminderDays, setReminderDays] = useState(7);
  /** ID of the job_applications row we ensured for this optimization (so we update instead of duplicate insert). */
  const [existingTrackingId, setExistingTrackingId] = useState<string | null>(null);
  const ensureTrackingDoneRef = useRef(false);

  // Ensure the job we optimized for is always in job_applications so it appears in Application Tracking
  useEffect(() => {
    if (!jobData?.title || !optimizationResult?.id || ensureTrackingDoneRef.current) return;

    const ensureJobTracked = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
        const jobTitle = (jobData?.title ?? '').trim();
        const companyName = (jobData?.company ?? '').trim() || 'Not specified';

        // Find existing row: by resume_version_id (if UUID) or by same job title + company (recent)
        if (isValidUUID(optimizationResult.id)) {
          const { data: existing } = await supabase
            .from('ggj_job_applications')
            .select('id')
            .eq('user_id', session.user.id)
            .eq('resume_version_id', optimizationResult.id)
            .maybeSingle();
          if (existing?.id) {
            setExistingTrackingId(existing.id);
            ensureTrackingDoneRef.current = true;
            return;
          }
        }

        const { data: existingByJob } = await supabase
          .from('ggj_job_applications')
          .select('id')
          .eq('user_id', session.user.id)
          .eq('job_title', jobTitle)
          .eq('company_name', companyName)
          .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (existingByJob?.id) {
          setExistingTrackingId(existingByJob.id);
          ensureTrackingDoneRef.current = true;
          return;
        }

        // Insert a row so this job appears in Application Tracking
        const insertPayload: Record<string, unknown> = {
          user_id: session.user.id,
          job_title: jobTitle,
          company_name: companyName,
          job_url: jobData?.url ?? '',
          location: jobData?.location || '',
          application_status: 'not_applied',
          application_notes: '',
          cover_letter_snippet: '',
          application_snippet: ''
        };
        if (isValidUUID(optimizationResult.id)) {
          insertPayload.resume_version_id = optimizationResult.id;
        }

        const { data: inserted, error } = await supabase
          .from('ggj_job_applications')
          .insert(insertPayload)
          .select('id')
          .single();

        if (!error && inserted?.id) {
          setExistingTrackingId(inserted.id);
          console.log('[ApplyStep] Ensured job tracked for Application Tracking, id:', inserted.id);
        }
      } catch (e) {
        console.warn('[ApplyStep] ensureJobTracked failed:', e);
      } finally {
        ensureTrackingDoneRef.current = true;
      }
    };

    ensureJobTracked();
  }, [jobData?.title, jobData?.company, jobData?.url, jobData?.location, optimizationResult?.id]);

  const generateApplicationSummary = async () => {
    if (!jobData) return;
    setSaveError(null);
    setGeneratingApplication(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-application-snippet', {
        body: {
          jobTitle: jobData.title,
          companyName: jobData.company,
          jobDescription: jobData.description,
          jobRequirements: jobData.requirements || [],
          jobSkills: jobData.required_skills || [],
          optimizationResult: optimizationResult,
          experienceLevel: 'Mid-Level'
        }
      });

      if (error) throw error;
      const snippet = (data?.applicationSnippet ?? (data as { data?: { applicationSnippet?: string } })?.data?.applicationSnippet) ?? '';
      setApplicationSnippet(snippet);
      if (!snippet) setSaveError('No application summary was returned. Please try again.');
    } catch (err) {
      console.error('[ApplyStep] Failed to generate application summary:', err);
      setSaveError('Failed to generate application summary. Please try again.');
    } finally {
      setGeneratingApplication(false);
    }
  };

  const generateCoverLetterNotes = async () => {
    if (!jobData) return;
    setSaveError(null);
    setGeneratingCoverLetter(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-cover-letter', {
        body: {
          jobTitle: jobData.title,
          companyName: jobData.company,
          jobDescription: jobData.description,
          jobRequirements: jobData.requirements || [],
          jobSkills: jobData.required_skills || [],
          resumeText: optimizationResult?.optimizedResume || optimizationResult?.originalResume,
          optimizationResult: optimizationResult,
          experienceLevel: 'Mid-Level'
        }
      });

      if (error) throw error;
      const letter = (data?.coverLetter ?? (data as { data?: { coverLetter?: string } })?.data?.coverLetter) ?? '';
      setCoverLetterSnippet(letter);
      if (!letter) setSaveError('No cover letter content was returned. Please try again.');
    } catch (err) {
      console.error('[ApplyStep] Failed to generate cover letter notes:', err);
      setSaveError('Failed to generate cover letter notes. Please try again.');
    } finally {
      setGeneratingCoverLetter(false);
    }
  };

  const handleApplyDirectly = () => {
    if (applicationMethod !== null && applicationMethod !== 'direct') return;
    if (!jobData?.url) {
      setSaveError('No job URL—add a job link above to open the posting, or use Snippets / Apply Later.');
      return;
    }
    setApplicationMethod('direct');
    setSaveError(null);
    window.open(jobData.url, '_blank');
    setTimeout(() => setShowConfirmation(true), 2000);
  };

  const handleUseSnippets = async () => {
    if (applicationMethod !== null && applicationMethod !== 'snippets') return;
    setApplicationMethod('snippets');
    setSaveError(null);
    const snippetText = `Application Summary:\n${applicationSnippet || '(Generate above)'}\n\nCover Letter:\n${coverLetterSnippet || '(Generate above)'}`;
    const ok = await safeClipboardWrite(snippetText);
    if (ok) {
      setSnippetCopied(true);
      setTimeout(() => setSnippetCopied(false), 3000);
    } else {
      setSaveError('Could not copy to clipboard.');
    }
    setShowConfirmation(true);
  };

  const handleApplyLater = () => {
    if (!applicationMethod) {
      // Lock this method to prevent multiple selections
      setApplicationMethod('later');
      setApplicationStatus('saved_for_later');
      setShowConfirmation(true);
    }
  };

  const handleSkip = async () => {
    setApplicationMethod('skip');
    try {
      const id = await saveApplication('not_applied');
      onNext(id ?? undefined);
    } catch {
      setApplied(true);
    }
  };

  const handleCancelMethod = () => {
    // Fire engagement email when user cancels the "Did You Apply?" modal (direct/snippets path only).
    // Skip for the "Save for Later" cancel — that has its own flow.
    if (applicationMethod === 'direct' || applicationMethod === 'snippets') {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session) return;
        console.log('[ApplyStep] Triggering apply_cancelled email → user cancelled Did You Apply?');
        supabase.functions.invoke('send-funnel-email', {
          body: {
            type: 'apply_cancelled',
            user_id: session.user.id,
            email: session.user.email,
            job_title: jobData?.title ?? '',
            company_name: jobData?.company ?? '',
          },
        }).then(({ data: d, error: e }) => {
          if (e) console.warn('[ApplyStep] apply_cancelled error:', e);
          else   console.log('[ApplyStep] apply_cancelled sent ✅ id:', d?.resend_id);
        }).catch((err) => console.warn('[ApplyStep] apply_cancelled network error:', err));
      });
    }
    // Allow user to cancel and choose different method
    setApplicationMethod(null);
    setShowConfirmation(false);
  };

  const handleConfirmApplied = async (didApply: boolean) => {
    setShowConfirmation(false);
    
    if (applicationMethod === 'later') {
      // For "Apply Later", always save as saved_for_later
      await saveApplication('saved_for_later');
    } else {
      // For "Direct" or "Snippets"
      if (didApply) {
        await saveApplication('applied');
      } else {
        await saveApplication('in_progress');
      }
    }
  };

  const saveApplication = async (status: ApplicationStatus): Promise<string | null> => {
    setApplying(true);
    setSaveError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Helper function to check if string is valid UUID
      const isValidUUID = (str: string) => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(str);
      };

      const insertData: any = {
        user_id: session.user.id,
        job_title: jobData?.title ?? '',
        company_name: jobData?.company ?? '',
        job_url: jobData?.url ?? '',
        location: jobData?.location || '',
        application_status: status,
        application_notes: applicationNotes,
        cover_letter_snippet: coverLetterSnippet,
        application_snippet: applicationSnippet
      };

      // Only set resume_version_id if it's a valid UUID (references llm_optimization_level2 table)
      // Custom IDs like "opt_1759566266959_4ssnizj7ppv" will cause UUID validation error
      if (optimizationResult?.id && isValidUUID(optimizationResult.id)) {
        insertData.resume_version_id = optimizationResult.id;
      } else if (optimizationResult?.id) {
        console.warn('[ApplyStep] Optimization ID is not a valid UUID, skipping resume_version_id:', optimizationResult.id);
      }

      // Add applied_date only if status is 'applied'
      if (status === 'applied') {
        insertData.applied_date = new Date().toISOString();
      }

      // Add reminder date for 'saved_for_later' – system will email user on this date
      if (status === 'saved_for_later') {
        const reminderDate = new Date();
        reminderDate.setDate(reminderDate.getDate() + reminderDays);
        const reminderNote = `\n\n[Reminder: Apply by ${reminderDate.toLocaleDateString()}]`;
        insertData.application_notes = `${applicationNotes}${reminderNote}`.trim();
        insertData.reminder_date = reminderDate.toISOString();
        insertData.job_saved_date = new Date().toISOString();
      }

      let data: { id: string } | null = null;

      if (existingTrackingId) {
        // Update the row we ensured for this optimization so we don't duplicate
        const updatePayload = { ...insertData };
        delete (updatePayload as Record<string, unknown>).user_id;
        const { data: updated, error: updateError } = await supabase
          .from('ggj_job_applications')
          .update(updatePayload)
          .eq('id', existingTrackingId)
          .select()
          .single();
        if (updateError) {
          console.error('[ApplyStep] Update error:', updateError);
          throw new Error(updateError.message || 'Failed to update application');
        }
        data = updated;
      } else {
        // Ensure didn't complete yet: avoid duplicate by updating if a matching row exists
        if (insertData.resume_version_id) {
          const { data: existing } = await supabase
            .from('ggj_job_applications')
            .select('id')
            .eq('user_id', session.user.id)
            .eq('resume_version_id', insertData.resume_version_id)
            .maybeSingle();
          if (existing?.id) {
            const updatePayload = { ...insertData };
            delete (updatePayload as Record<string, unknown>).user_id;
            const { data: updated, error: updateError } = await supabase
              .from('ggj_job_applications')
              .update(updatePayload)
              .eq('id', existing.id)
              .select()
              .single();
            if (!updateError && updated) {
              data = updated;
              setExistingTrackingId(existing.id);
            }
          }
        }
        if (!data) {
          const result = await supabase
            .from('ggj_job_applications')
            .insert(insertData)
            .select()
            .single();
          if (result.error) {
            console.error('[ApplyStep] Database error:', result.error);
            throw new Error(result.error.message || 'Failed to save application');
          }
          data = result.data;
        }
      }

      console.log('[ApplyStep] Application saved with ID:', data?.id, 'Status:', status);
      setSavedApplicationId(data?.id || null);
      setApplicationStatus(status);
      setApplied(true);
      setSaveError(null);

      // Fire-and-forget engagement emails based on application status
      const _emailUserId  = session.user.id;
      const _emailAddress = session.user.email;
      const _emailJob     = jobData?.title    ?? '';
      const _emailCompany = jobData?.company  ?? '';

      if (status === 'applied') {
        console.log('[ApplyStep] Triggering apply_confirmation email → to:', _emailAddress, '| job:', _emailJob, '@', _emailCompany);
        supabase.functions.invoke('send-funnel-email', {
          body: { type: 'apply_confirmation', user_id: _emailUserId, email: _emailAddress, job_title: _emailJob, company_name: _emailCompany },
        }).then(({ data: d, error: e }) => {
          if (e) console.warn('[ApplyStep] apply_confirmation error:', e);
          else   console.log('[ApplyStep] apply_confirmation sent ✅ id:', d?.resend_id);
        }).catch((err) => console.warn('[ApplyStep] apply_confirmation network error:', err));
      }

      if (status === 'saved_for_later') {
        const _reminderDate = insertData.reminder_date as string | undefined;
        console.log('[ApplyStep] Triggering save_for_later_confirmation email → reminder in', reminderDays, 'days');
        supabase.functions.invoke('send-funnel-email', {
          body: {
            type: 'save_for_later_confirmation',
            user_id: _emailUserId,
            email: _emailAddress,
            job_title: _emailJob,
            company_name: _emailCompany,
            reminder_days: reminderDays,
            reminder_date: _reminderDate,
          },
        }).then(({ data: d, error: e }) => {
          if (e) console.warn('[ApplyStep] save_for_later_confirmation error:', e);
          else   console.log('[ApplyStep] save_for_later_confirmation sent ✅ id:', d?.resend_id);
        }).catch((err) => console.warn('[ApplyStep] save_for_later_confirmation network error:', err));
      }

      if (status === 'in_progress') {
        console.log('[ApplyStep] Triggering apply_not_yet email → user chose Not Yet');
        supabase.functions.invoke('send-funnel-email', {
          body: { type: 'apply_not_yet', user_id: _emailUserId, email: _emailAddress, job_title: _emailJob, company_name: _emailCompany },
        }).then(({ data: d, error: e }) => {
          if (e) console.warn('[ApplyStep] apply_not_yet error:', e);
          else   console.log('[ApplyStep] apply_not_yet sent ✅ id:', d?.resend_id);
        }).catch((err) => console.warn('[ApplyStep] apply_not_yet network error:', err));
      }
    } catch (err) {
      console.error('Failed to save application:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save application. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleNext = async () => {
    if (applying || isLoading) return;
    if (applied) {
      console.log('[ApplyStep] Proceeding with application ID:', savedApplicationId, 'Status:', applicationStatus);
      onNext(savedApplicationId || undefined);
    } else {
      onNext(undefined);
    }
  };

  const getStatusIcon = () => {
    switch (applicationStatus) {
      case 'applied':
        return <CheckCircle className="w-6 h-6" style={{ color: 'var(--vscode-success)' }} />;
      case 'in_progress':
        return <Clock className="w-6 h-6" style={{ color: 'var(--vscode-info)' }} />;
      case 'saved_for_later':
        return <BookmarkPlus className="w-6 h-6" style={{ color: 'var(--vscode-primary)' }} />;
      case 'not_applied':
        return <Info className="w-6 h-6 vscode-text-tertiary" />;
      default:
        return null;
    }
  };

  const getStatusMessage = () => {
    switch (applicationStatus) {
      case 'applied':
        return {
          title: t('optimizer.applyStep.statusSubmittedTitle'),
          message: t('optimizer.applyStep.statusSubmittedDesc'),
          color: 'cyan'
        };
      case 'in_progress':
        return {
          title: t('optimizer.applyStep.statusInProgressTitle'),
          message: t('optimizer.applyStep.statusInProgressDesc'),
          color: 'blue'
        };
      case 'saved_for_later':
        return {
          title: t('optimizer.applyStep.statusSavedTitle'),
          message: t('optimizer.applyStep.statusSavedDesc', { days: reminderDays }),
          color: 'blue'
        };
      case 'not_applied':
        return {
          title: t('optimizer.applyStep.statusSkippedTitle'),
          message: t('optimizer.applyStep.statusSkippedDesc'),
          color: 'gray'
        };
      default:
        return { title: '', message: '', color: 'gray' };
    }
  };

  const primaryButtonClass = 'bg-[var(--vscode-primary)] text-[var(--vscode-primary-text)] hover:opacity-90 transition-opacity font-semibold';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className={isNative ? '' : STEP_MOTION_CLASS}
      style={isNative ? { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', pointerEvents: 'auto', isolation: 'isolate' as const } : { pointerEvents: 'auto' }}
    >
      {/* Confirmation Modal — rendered via portal at document.body so CSS transforms on parents can't trap the fixed overlay */}
      {showConfirmation && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 pb-[env(safe-area-inset-bottom)] bg-black/60 backdrop-blur-md"
            style={{ pointerEvents: 'all' }}
            onClick={() => setShowConfirmation(false)}
          >
            <motion.div
              initial={{ y: 32, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 32, opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-[400px] overflow-hidden max-h-[92vh] flex flex-col"
              style={{ background: 'var(--vscode-bg-elevated)', border: '1px solid var(--vscode-border-primary)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {applicationMethod === 'later' ? (
                /* ── SAVE FOR LATER modal — native mobile design ── */
                <>
                  {/* Gradient title bar */}
                  <div className="flex items-center gap-3 px-5 shrink-0" style={{ ...MODAL_ACCENT, paddingTop: 20, paddingBottom: 18 }}>
                    <div className="w-10 h-10 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center shadow-sm shrink-0">
                      <Zap className="w-5 h-5 text-white" fill="white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold text-white/60 uppercase tracking-[0.15em] leading-none mb-1">Resume Optimizer</p>
                      <h3 className="text-[17px] font-bold text-white leading-tight">{t('optimizer.applyStep.saveForLaterTitle')}</h3>
                    </div>
                    <button
                      type="button"
                      onClick={handleCancelMethod}
                      className="w-9 h-9 flex items-center justify-center rounded-xl touch-manipulation shrink-0"
                      style={{ background: 'rgba(255,255,255,0.18)', border: 'none' }}
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="px-5 pt-5 pb-2 flex-1 overflow-y-auto">
                    {/* Info notice — compact */}
                    <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-2xl mb-5" style={{ background: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)' }}>
                      <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--vscode-primary)' }} />
                      <p className="text-sm leading-snug" style={{ color: 'var(--vscode-text-primary)' }}>
                        <strong>{t('optimizer.applyStep.saveForLaterNote')}</strong> {t('optimizer.applyStep.saveForLaterExpiry')}
                      </p>
                    </div>

                    {/* Reminder selector */}
                    <p className="text-sm font-bold mb-3" style={{ color: 'var(--vscode-text-primary, #111)' }}>{t('optimizer.applyStep.remindMeIn')}</p>
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      {[3, 7, 14].map((days) => {
                        const active = reminderDays === days;
                        return (
                          <button
                            type="button"
                            key={days}
                            onClick={() => setReminderDays(days)}
                            style={{
                              minHeight: 54,
                              borderRadius: 16,
                              border: `2px solid ${active ? 'var(--vscode-primary)' : 'var(--vscode-border-primary)'}`,
                              background: active ? 'var(--brand-accent-bg)' : 'transparent',
                              color: active ? 'var(--vscode-primary)' : 'var(--vscode-text-secondary, #666)',
                              fontSize: 14,
                              fontWeight: 700,
                              cursor: 'pointer',
                              WebkitTapHighlightColor: 'transparent',
                              boxShadow: active ? 'var(--vscode-shadow-sm)' : 'none',
                            }}
                          >
                            {days} {t('optimizer.applyStep.days')}
                          </button>
                        );
                      })}
                    </div>

                    {/* Primary action */}
                    <button
                      type="button"
                      onClick={() => handleConfirmApplied(false)}
                      disabled={applying}
                      style={{
                        width: '100%', minHeight: 52, borderRadius: 16,
                        background: 'var(--brand-cta-bg)',
                        border: 'none', color: 'var(--vscode-primary-foreground, #fff)', fontSize: 15, fontWeight: 700,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        boxShadow: 'var(--brand-cta-shadow)', marginBottom: 12,
                        opacity: applying ? 0.6 : 1,
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      {applying ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> {t('optimizer.applyStep.savingEllipsis')}</>
                      ) : <>{t('optimizer.applyStep.saveForLaterBtn')}</>}
                    </button>

                    {/* Cancel text link */}
                    <button
                      type="button"
                      onClick={handleCancelMethod}
                      style={{ width: '100%', minHeight: 40, background: 'none', border: 'none', color: 'var(--vscode-text-tertiary, #888)', fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 4 }}
                    >
                      {t('optimizer.applyStep.cancel', 'Cancel')}
                    </button>
                  </div>

                  <div className="px-5 pb-4 shrink-0">
                    <p className="text-xs text-center" style={{ color: 'var(--vscode-text-tertiary, #aaa)' }}>
                      {t('optimizer.applyStep.reminderHint')}
                    </p>
                  </div>
                </>
              ) : (
                /* ── DID YOU APPLY modal — native mobile design ── */
                <>
                  {/* Gradient title bar */}
                  <div className="flex items-center gap-3 px-5 shrink-0" style={{ ...MODAL_ACCENT, paddingTop: 20, paddingBottom: 18 }}>
                    <div className="w-10 h-10 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center shadow-sm shrink-0">
                      <Zap className="w-5 h-5 text-white" fill="white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold text-white/60 uppercase tracking-[0.15em] leading-none mb-1">Resume Optimizer</p>
                      <h3 className="text-[17px] font-bold text-white leading-tight">{t('optimizer.applyStep.didYouApply', 'Did You Apply?')}</h3>
                    </div>
                    <button
                      type="button"
                      onClick={handleCancelMethod}
                      className="w-9 h-9 flex items-center justify-center rounded-xl touch-manipulation shrink-0"
                      style={{ background: 'rgba(255,255,255,0.18)', border: 'none' }}
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="px-5 pt-5 pb-2 flex-1 overflow-y-auto">
                    <p className="text-sm mb-4" style={{ color: 'var(--vscode-text-secondary, #666)' }}>
                      {t('optimizer.applyStep.helpUsTrack', 'Help us track your application')}
                    </p>

                    {/* Question chip */}
                    <div className="flex items-start gap-2.5 px-3.5 py-3.5 rounded-2xl mb-6" style={{ background: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)' }}>
                      <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--vscode-primary)' }} />
                      <p className="text-sm leading-snug" style={{ color: 'var(--vscode-text-primary, #111)' }}>
                        {applicationMethod === 'direct'
                          ? t('optimizer.applyStep.confirmQuestionDirect', 'Did you submit your application on the job portal?')
                          : t('optimizer.applyStep.confirmQuestionSnippets', 'Did you submit your application using the generated snippets?')}
                      </p>
                    </div>

                    {/* Primary action — Yes, Applied */}
                    <button
                      type="button"
                      onClick={() => handleConfirmApplied(true)}
                      disabled={applying}
                      style={{
                        width: '100%', minHeight: 52, borderRadius: 16,
                        background: 'var(--brand-cta-bg)',
                        border: 'none', color: 'var(--vscode-primary-foreground, #fff)', fontSize: 15, fontWeight: 700,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        boxShadow: 'var(--brand-cta-shadow)', marginBottom: 10,
                        opacity: applying ? 0.6 : 1,
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : t('optimizer.applyStep.yesApplied', 'Yes, Applied')}
                    </button>

                    {/* Secondary — Not Yet */}
                    <button
                      type="button"
                      onClick={() => handleConfirmApplied(false)}
                      disabled={applying}
                      style={{
                        width: '100%', minHeight: 50, borderRadius: 16,
                        background: 'transparent',
                        border: '2px solid var(--brand-accent-border)',
                        color: 'var(--vscode-primary)', fontSize: 15, fontWeight: 700,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: 12,
                        opacity: applying ? 0.6 : 1,
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : t('optimizer.applyStep.notYet', 'Not Yet')}
                    </button>

                    {/* Cancel text link */}
                    <button
                      type="button"
                      onClick={handleCancelMethod}
                      style={{ width: '100%', minHeight: 40, background: 'none', border: 'none', color: 'var(--vscode-text-tertiary, #888)', fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 4 }}
                    >
                      {t('optimizer.applyStep.cancel', 'Cancel')}
                    </button>
                  </div>

                  <div className="px-5 pb-4 shrink-0">
                    <p className="text-xs text-center" style={{ color: 'var(--vscode-text-tertiary, #aaa)' }}>
                      {t('optimizer.applyStep.confirmHint', 'Choose "Not Yet" to mark as in-progress. You can update status later from dashboard.')}
                    </p>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* ── Preview Modal ── */}
      {previewModal && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
            style={{ pointerEvents: 'all' }}
            onClick={() => { setPreviewModal(null); setPreviewCopied(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="rounded-2xl shadow-2xl w-full max-w-[540px] overflow-hidden flex flex-col"
              style={{ maxHeight: '85vh', background: 'var(--vscode-bg-elevated)', border: '1px solid var(--vscode-border-primary)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Title bar — theme primary (no hardcoded purple) */}
              <div
                className="flex items-center gap-3 px-5 py-4 shrink-0"
                style={{ background: 'var(--brand-popup-header, var(--ggj-header-bg, var(--vscode-primary)))' }}
              >
                <div className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center shadow-sm shrink-0">
                  {previewModal === 'summary'
                    ? <Clipboard className="w-5 h-5 text-white" />
                    : <FileText className="w-5 h-5 text-white" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-white/60 uppercase tracking-[0.15em] leading-none mb-1">Resume Optimizer</p>
                  <h3 className="text-base font-bold text-white leading-tight truncate">
                    {previewModal === 'summary'
                      ? t('optimizer.applyStep.applicationSummary')
                      : t('optimizer.applyStep.coverLetterNotes')
                    }
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => { setPreviewModal(null); setPreviewCopied(false); }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white hover:bg-white/15 transition-colors touch-manipulation shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--vscode-text-primary)' }}>
                  {previewModal === 'summary' ? applicationSnippet : coverLetterSnippet}
                </p>
              </div>

              {/* Footer actions */}
              <div className="px-5 py-3 border-t shrink-0 flex gap-2.5" style={{ borderColor: 'var(--vscode-border-divider)' }}>
                <button
                  type="button"
                  onClick={async () => {
                    const text = previewModal === 'summary' ? applicationSnippet : coverLetterSnippet;
                    const ok = await safeClipboardWrite(text ?? '');
                    if (ok) {
                      setPreviewCopied(true);
                      setTimeout(() => setPreviewCopied(false), 2500);
                    }
                  }}
                  className="flex-1 min-h-[44px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors touch-manipulation"
                  style={{ border: '1px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-tertiary)', color: 'var(--vscode-text-primary)' }}
                >
                  {previewCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {previewCopied ? 'Copied!' : 'Copy to Clipboard'}
                </button>
                <button
                  type="button"
                  onClick={() => { setPreviewModal(null); setPreviewCopied(false); }}
                  className="flex-1 min-h-[44px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90 touch-manipulation"
                  style={{ background: 'var(--brand-cta-bg)', color: 'var(--vscode-primary-foreground, #fff)' }}
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* ══════════════════════════════════════════
          NATIVE MOBILE — full redesign
          ══════════════════════════════════════════ */}
      {isNative && (<>
        {/* Purple gradient splash header */}
        <div
          className="flex-shrink-0"
          style={{
            background: 'var(--ggj-header-bg)',
            paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
            borderRadius: '0 0 24px 24px',
            boxShadow: 'var(--brand-cta-shadow, 0 4px 20px rgba(108,71,255,0.28))',
          }}
        >
          <div className="flex items-center justify-between px-4 pb-4 gap-2">
            <button type="button" onClick={onBack}
              className="w-9 h-9 flex items-center justify-center rounded-xl touch-manipulation flex-shrink-0"
              style={{ background: 'var(--ggj-header-btn-bg)', color: 'var(--ggj-header-btn-icon)' }}>
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center flex-1 min-w-0">
              <span className="text-[10px] font-bold uppercase tracking-widest leading-none" style={{ color: 'rgba(255,255,255,0.65)' }}>Resume Optimizer</span>
              <span className="text-base font-extrabold leading-tight mt-0.5 text-white">Apply for Job</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)' }}>
              <Calendar className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.85)' }} />
              <span className="text-[11px] font-semibold" style={{ color: 'rgba(255,255,255,0.90)' }}>{new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto" style={{ background: 'var(--ggj-bg-page)', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
          <div className="px-4 pt-4 pb-4 space-y-4">

            {/* Job card */}
            <div className="rounded-2xl border overflow-hidden"
              style={{ background: 'var(--ggj-bg-card)', borderColor: 'var(--ggj-border)', boxShadow: 'var(--ggj-shadow-card)' }}>
              <div className="flex items-center gap-3 px-4 py-3.5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'var(--brand-accent-bg)', color: 'var(--vscode-primary)' }}>
                  <Briefcase className="w-5 h-5" style={{ color: 'var(--vscode-primary)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate" style={{ color: 'var(--ggj-text-heading)' }}>{jobData?.title ?? '—'}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs font-medium" style={{ color: 'var(--ggj-text-secondary)' }}>{jobData?.company ?? '—'}</span>
                    {jobData?.location && (
                      <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--ggj-text-muted)' }}>
                        <MapPin className="w-3 h-3" />{jobData.location}
                      </span>
                    )}
                  </div>
                </div>
                {jobData?.url && (
                  <a href={jobData.url} target="_blank" rel="noopener noreferrer"
                    className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0 touch-manipulation"
                    style={{ background: 'var(--brand-accent-bg)', color: 'var(--vscode-primary)' }}>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>

            {/* Error */}
            {saveError && (
              <div className="rounded-2xl border px-4 py-3 flex items-start gap-3"
                style={{ background: 'rgba(239,68,68,0.06)', borderColor: 'rgba(239,68,68,0.25)' }}>
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-600">Error</p>
                  <p className="text-xs text-red-500 mt-0.5">{saveError}</p>
                </div>
                <button type="button" onClick={() => setSaveError(null)} className="w-6 h-6 flex items-center justify-center rounded-lg touch-manipulation" style={{ color: 'var(--ggj-text-muted)' }}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Applied status badge */}
            {applied && (
              <div className="rounded-2xl border px-4 py-3.5 flex items-center gap-3"
                style={{ background: 'var(--vscode-success-bg)', borderColor: 'var(--vscode-success)' }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'var(--brand-accent-bg)', color: 'var(--vscode-primary)' }}>
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--vscode-primary)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>{getStatusMessage().title}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--ggj-text-secondary)' }}>{getStatusMessage().message}</p>
                </div>
              </div>
            )}

            {/* Application Summary card */}
            <div className="rounded-2xl border overflow-hidden"
              style={{ background: 'var(--ggj-bg-card)', borderColor: 'var(--ggj-border)' }}>
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--ggj-border)' }}>
                <div className="flex items-center gap-2">
                  <Clipboard className="w-4 h-4" style={{ color: 'var(--vscode-primary)' }} />
                  <span className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>Application Summary</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {applicationSnippet && !generatingApplication && (
                    <button type="button" onClick={() => setPreviewModal('summary')}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold touch-manipulation"
                      style={{ background: 'var(--brand-accent-bg)', color: 'var(--vscode-primary)' }}>
                      <Eye className="w-3 h-3" /> Preview
                    </button>
                  )}
                  <button type="button" onClick={generateApplicationSummary}
                    disabled={generatingApplication || isLoading}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold touch-manipulation disabled:opacity-50"
                    style={{ background: 'var(--brand-cta-bg)', color: 'var(--vscode-primary-foreground, #fff)' }}>
                    {generatingApplication ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {generatingApplication ? 'Generating…' : (applicationSnippet ? 'Regen' : 'Generate')}
                  </button>
                </div>
              </div>
              <div className="relative px-4 py-3">
                <textarea
                  value={applicationSnippet}
                  onChange={(e) => setApplicationSnippet(e.target.value)}
                  rows={3}
                  className="w-full text-sm resize-none bg-transparent outline-none leading-relaxed"
                  placeholder="Brief summary (AI-generated)…"
                  style={{ color: 'var(--ggj-text-primary)' }}
                  readOnly={generatingApplication}
                />
                {generatingApplication && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/5">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--vscode-primary)' }} />
                  </div>
                )}
              </div>
            </div>

            {/* Cover Letter card */}
            <div className="rounded-2xl border overflow-hidden"
              style={{ background: 'var(--ggj-bg-card)', borderColor: 'var(--ggj-border)' }}>
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--ggj-border)' }}>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" style={{ color: 'var(--vscode-primary)' }} />
                  <span className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>Cover Letter Notes</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {coverLetterSnippet && !generatingCoverLetter && (
                    <button type="button" onClick={() => setPreviewModal('cover')}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold touch-manipulation"
                      style={{ background: 'var(--brand-accent-bg)', color: 'var(--vscode-primary)' }}>
                      <Eye className="w-3 h-3" /> Preview
                    </button>
                  )}
                  <button type="button" onClick={generateCoverLetterNotes}
                    disabled={generatingCoverLetter || isLoading}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold touch-manipulation disabled:opacity-50"
                    style={{ background: 'var(--brand-cta-bg)', color: 'var(--vscode-primary-foreground, #fff)' }}>
                    {generatingCoverLetter ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {generatingCoverLetter ? 'Generating…' : (coverLetterSnippet ? 'Regen' : 'Generate')}
                  </button>
                </div>
              </div>
              <div className="relative px-4 py-3">
                <textarea
                  value={coverLetterSnippet}
                  onChange={(e) => setCoverLetterSnippet(e.target.value)}
                  rows={3}
                  className="w-full text-sm resize-none bg-transparent outline-none leading-relaxed"
                  placeholder="Key points (AI-generated)…"
                  style={{ color: 'var(--ggj-text-primary)' }}
                  readOnly={generatingCoverLetter}
                />
                {generatingCoverLetter && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/5">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--vscode-primary)' }} />
                  </div>
                )}
              </div>
            </div>

            {/* Additional Notes */}
            <div className="rounded-2xl border overflow-hidden"
              style={{ background: 'var(--ggj-bg-card)', borderColor: 'var(--ggj-border)' }}>
              <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--ggj-border)' }}>
                <Info className="w-4 h-4" style={{ color: 'var(--vscode-primary)' }} />
                <span className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>Additional Notes</span>
              </div>
              <div className="px-4 py-3">
                <textarea
                  value={applicationNotes}
                  onChange={(e) => setApplicationNotes(e.target.value)}
                  rows={2}
                  className="w-full text-sm resize-none bg-transparent outline-none leading-relaxed"
                  placeholder="Add any notes…"
                  style={{ color: 'var(--ggj-text-primary)' }}
                />
              </div>
            </div>

            {/* How to Apply — 3 action tiles */}
            {!applied && (
              <div>
                <p className="text-xs font-bold uppercase tracking-widest mb-3 px-1" style={{ color: 'var(--ggj-text-muted)' }}>How to Apply</p>
                <div className="grid grid-cols-3 gap-2.5">
                  {/* Direct */}
                  <button type="button"
                    onClick={handleApplyDirectly}
                    disabled={applying || !jobData?.url || (applicationMethod !== null && applicationMethod !== 'direct')}
                    className="flex flex-col items-center gap-2 p-3.5 rounded-2xl border touch-manipulation transition-all disabled:opacity-40"
                    style={applicationMethod === 'direct' ? {
                      background: 'var(--brand-cta-bg)',
                      borderColor: 'transparent',
                      boxShadow: 'var(--brand-cta-shadow)',
                    } : {
                      background: 'var(--ggj-bg-card)',
                      borderColor: 'var(--ggj-border)',
                    }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={applicationMethod === 'direct'
                        ? { background: 'rgba(255,255,255,0.20)' }
                        : { background: 'var(--brand-accent-bg)' }}>
                      <ExternalLink className="w-4.5 h-4.5" style={{ color: applicationMethod === 'direct' ? 'var(--vscode-primary-foreground, #fff)' : 'var(--vscode-primary)' }} />
                    </div>
                    <span className="text-[11px] font-bold text-center leading-tight"
                      style={{ color: applicationMethod === 'direct' ? 'var(--vscode-primary-foreground, #fff)' : 'var(--ggj-text-primary)' }}>
                      Direct
                    </span>
                  </button>

                  {/* Snippets */}
                  <button type="button"
                    onClick={handleUseSnippets}
                    disabled={applying || (applicationMethod !== null && applicationMethod !== 'snippets')}
                    className="flex flex-col items-center gap-2 p-3.5 rounded-2xl border touch-manipulation transition-all disabled:opacity-40"
                    style={applicationMethod === 'snippets' ? {
                      background: 'var(--brand-cta-bg)',
                      borderColor: 'transparent',
                      boxShadow: 'var(--brand-cta-shadow)',
                    } : {
                      background: 'var(--ggj-bg-card)',
                      borderColor: 'var(--ggj-border)',
                    }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={applicationMethod === 'snippets'
                        ? { background: 'rgba(255,255,255,0.20)' }
                        : { background: 'var(--brand-accent-bg)' }}>
                      {snippetCopied
                        ? <Check className="w-4.5 h-4.5" style={{ color: applicationMethod === 'snippets' ? 'var(--vscode-primary-foreground, #fff)' : 'var(--vscode-success)' }} />
                        : <Clipboard className="w-4.5 h-4.5" style={{ color: applicationMethod === 'snippets' ? 'var(--vscode-primary-foreground, #fff)' : 'var(--vscode-primary)' }} />
                      }
                    </div>
                    <span className="text-[11px] font-bold text-center leading-tight"
                      style={{ color: applicationMethod === 'snippets' ? 'var(--vscode-primary-foreground, #fff)' : 'var(--ggj-text-primary)' }}>
                      {snippetCopied ? 'Copied!' : 'Snippets'}
                    </span>
                  </button>

                  {/* Save for Later */}
                  <button type="button"
                    onClick={handleApplyLater}
                    disabled={applying || (applicationMethod !== null && applicationMethod !== 'later')}
                    className="flex flex-col items-center gap-2 p-3.5 rounded-2xl border touch-manipulation transition-all disabled:opacity-40"
                    style={applicationMethod === 'later' ? {
                      background: 'var(--brand-cta-bg)',
                      borderColor: 'transparent',
                      boxShadow: 'var(--brand-cta-shadow)',
                    } : {
                      background: 'var(--ggj-bg-card)',
                      borderColor: 'var(--ggj-border)',
                    }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={applicationMethod === 'later'
                        ? { background: 'rgba(255,255,255,0.20)' }
                        : { background: 'var(--brand-accent-bg)' }}>
                      <BookmarkPlus className="w-4.5 h-4.5" style={{ color: applicationMethod === 'later' ? 'var(--vscode-primary-foreground, #fff)' : 'var(--vscode-primary)' }} />
                    </div>
                    <span className="text-[11px] font-bold text-center leading-tight"
                      style={{ color: applicationMethod === 'later' ? 'var(--vscode-primary-foreground, #fff)' : 'var(--ggj-text-primary)' }}>
                      Save Later
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Bottom spacer */}
            <div className="h-1" />
          </div>
        </div>

        {/* Bottom CTA bar */}
        <div className="flex-shrink-0 px-4 pt-3 border-t"
          style={{
            background: 'var(--ggj-nav-bg)',
            borderColor: 'var(--ggj-border)',
            backdropFilter: 'blur(12px)',
            paddingBottom: 12,
          }}>
          <div className="flex items-center gap-3">
            {/* Skip — only before applied */}
            {!applied && (
              <button type="button"
                onClick={handleSkip}
                disabled={applying || isLoading}
                className="flex items-center justify-center gap-1.5 px-5 py-3.5 rounded-2xl border text-sm font-semibold touch-manipulation disabled:opacity-50 flex-shrink-0"
                style={{ background: 'var(--ggj-bg-card)', borderColor: 'var(--ggj-border)', color: 'var(--ggj-text-secondary)' }}>
                Skip
              </button>
            )}
            {/* Continue / Track */}
            <button type="button"
              onClick={handleNext}
              disabled={applying || isLoading}
              className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl text-sm font-bold touch-manipulation disabled:opacity-50"
              style={{
                background: 'var(--brand-cta-bg)',
                color: 'var(--vscode-primary-foreground, #fff)',
                boxShadow: 'var(--brand-cta-shadow)',
              }}>
              {applying && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
              <span>{applied ? 'Track Application' : 'Continue'}</span>
              {!applying && <ArrowRight className="w-4 h-4 shrink-0" />}
            </button>
          </div>
        </div>
      </>)}

      {/* ══════════════════════════════════════════
          WEB / NON-NATIVE render (unchanged)
          ══════════════════════════════════════════ */}
      {!isNative && (
      <div className={STEP_WRAPPER_CLASS}>
        <div className={STEP_SCROLL_AREA_CLASS}>
          <div className={STEP_CARD_COMPACT_CLASS}>
            <div className="grid gap-0 min-w-0 grid-cols-1 lg:grid-cols-[1.75fr_0.85fr]">
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-3 px-4 sm:px-5 lg:px-6 py-3 shrink-0 border-b vscode-border vscode-bg-secondary">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--brand-cta-bg)' }}>
                  <Send className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] font-bold vscode-text-secondary uppercase tracking-[0.15em] leading-none mb-0.5">Resume Optimizer</p>
                  <h2 className="text-sm font-bold vscode-text leading-tight">{t('optimizer.applyStep.title')}</h2>
                </div>
                <div className="vscode-bg rounded-lg border vscode-border px-2 py-1 flex items-center gap-1.5 text-xs vscode-text-secondary shrink-0">
                  <Calendar className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{new Date().toLocaleString()}</span>
                  <span className="sm:hidden">{new Date().toLocaleDateString()}</span>
                </div>
              </div>
              <div className="p-4 sm:p-5 lg:p-6 pb-10 sm:pb-6 lg:pb-6 flex flex-col min-w-0">
                <div className="rounded-lg border p-2.5 mb-3 flex flex-wrap items-center gap-x-3 gap-y-1" style={{ background: 'var(--brand-accent-bg)', borderColor: 'var(--brand-accent-border)' }}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Building className="w-4 h-4 shrink-0" style={{ color: 'var(--vscode-primary)' }} />
                    <span className="vscode-text-primary font-medium text-sm truncate">{jobData?.title ?? '—'}</span>
                  </div>
                  <span className="vscode-text-tertiary text-xs">{jobData?.company ?? '—'}</span>
                  {jobData?.location && <span className="flex items-center gap-1 text-xs vscode-text-tertiary"><MapPin className="w-3 h-3" />{jobData.location}</span>}
                  {jobData?.url && <a href={jobData.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs" style={{ color: 'var(--vscode-primary)' }}><LinkIcon className="w-3 h-3" /><span>{t('optimizer.applyStep.viewJob')}</span><ExternalLink className="w-3 h-3" /></a>}
                </div>
                {saveError && (
                  <div className="bg-orange-900/50 border border-orange-700 rounded-lg p-2 mb-2 flex items-start space-x-2">
                    <AlertCircle className="w-3.5 h-3.5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1"><p className="text-orange-300 text-xs font-medium">{t('optimizer.applyStep.error')}</p><p className="text-orange-200 text-xs mt-0.5">{saveError}</p><button onClick={() => setSaveError(null)} className="text-orange-400 text-xs underline mt-0.5">{t('optimizer.applyStep.dismiss')}</button></div>
                  </div>
                )}
                <div className="space-y-2 mb-2">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium vscode-text-secondary flex items-center gap-1"><Clipboard className="w-3.5 h-3.5" /> {t('optimizer.applyStep.applicationSummary')}</label>
                      <div className="flex items-center gap-1">
                        {applicationSnippet && !generatingApplication && <button type="button" onClick={() => setPreviewModal('summary')} className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium text-white" style={{ background: 'var(--brand-cta-bg)' }}><Eye className="w-3 h-3" /> Preview</button>}
                        <button type="button" onClick={generateApplicationSummary} disabled={generatingApplication || isLoading} className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium disabled:opacity-50" style={{ background: 'var(--vscode-primary-bg)', color: 'var(--vscode-primary)' }}>
                          {generatingApplication ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}{generatingApplication ? t('optimizer.applyStep.generating') : (applicationSnippet ? 'Regenerate' : t('optimizer.applyStep.generateSummary'))}
                        </button>
                      </div>
                    </div>
                    <div className="relative"><textarea value={applicationSnippet} onChange={(e) => setApplicationSnippet(e.target.value)} rows={3} className="w-full px-2 py-1.5 text-sm vscode-bg-tertiary border vscode-border rounded-lg vscode-text-primary focus:outline-none resize-none" placeholder={t('optimizer.applyStep.applicationSummaryPlaceholder')} readOnly={generatingApplication} style={generatingApplication ? { opacity: 0.7 } : {}} />{generatingApplication && <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-lg"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--vscode-primary)' }} /></div>}</div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium vscode-text-secondary flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {t('optimizer.applyStep.coverLetterNotes')}</label>
                      <div className="flex items-center gap-1">
                        {coverLetterSnippet && !generatingCoverLetter && <button type="button" onClick={() => setPreviewModal('cover')} className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium text-white" style={{ background: 'var(--brand-cta-bg)' }}><Eye className="w-3 h-3" /> Preview</button>}
                        <button type="button" onClick={generateCoverLetterNotes} disabled={generatingCoverLetter || isLoading} className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium disabled:opacity-50" style={{ background: 'var(--vscode-primary-bg)', color: 'var(--vscode-primary)' }}>
                          {generatingCoverLetter ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}{generatingCoverLetter ? t('optimizer.applyStep.generating') : (coverLetterSnippet ? 'Regenerate' : t('optimizer.applyStep.generateCover'))}
                        </button>
                      </div>
                    </div>
                    <div className="relative"><textarea value={coverLetterSnippet} onChange={(e) => setCoverLetterSnippet(e.target.value)} rows={3} className="w-full px-2 py-1.5 text-sm vscode-bg-tertiary border vscode-border rounded-lg vscode-text-primary focus:outline-none resize-none" placeholder={t('optimizer.applyStep.coverLetterPlaceholder')} readOnly={generatingCoverLetter} style={generatingCoverLetter ? { opacity: 0.7 } : {}} />{generatingCoverLetter && <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-lg"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--vscode-primary)' }} /></div>}</div>
                  </div>
                  <div>
                    <label className="text-xs font-medium vscode-text-secondary mb-0.5 block">{t('optimizer.applyStep.additionalNotes')}</label>
                    <textarea value={applicationNotes} onChange={(e) => setApplicationNotes(e.target.value)} rows={2} className="w-full px-2 py-1.5 text-sm vscode-bg-tertiary border vscode-border rounded-lg vscode-text-primary focus:outline-none resize-none" placeholder={t('optimizer.applyStep.additionalNotesPlaceholder')} />
                  </div>
                </div>
                {applied && (
                  <div className="rounded-lg border vscode-border px-4 py-3 mb-4 flex items-center gap-3" style={{ borderColor: 'var(--vscode-success)', backgroundColor: 'var(--vscode-success-bg)' }}>
                    <div className="shrink-0">{getStatusIcon()}</div>
                    <div><p className="text-sm font-medium vscode-text-primary">{getStatusMessage().title}</p><p className="text-xs vscode-text-secondary mt-0.5">{getStatusMessage().message}</p></div>
                  </div>
                )}
                {!applied && (
                  <div className="mb-3 pb-1">
                    <p className="text-xs font-medium vscode-text-secondary mb-1.5">{t('optimizer.applyStep.howToApply')}</p>
                    <div className="flex rounded-xl touch-manipulation min-h-[48px] overflow-visible gap-1" role="group" aria-label="Apply method">
                      <button type="button" onClick={handleApplyDirectly} disabled={applying || !jobData?.url || (applicationMethod !== null && applicationMethod !== 'direct')} style={{ ...(!jobData?.url ? { backgroundColor: 'var(--vscode-input-background)', color: 'var(--vscode-text-tertiary)', cursor: 'not-allowed', opacity: 0.7 } : applicationMethod === 'direct' ? { background: 'var(--vscode-primary)', color: 'var(--vscode-primary-text)' } : applicationMethod === null ? { background: 'var(--vscode-primary-bg)', color: 'var(--vscode-primary)' } : { background: 'var(--vscode-input-background)', color: 'var(--vscode-text-tertiary)', opacity: 0.6 }) }} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-3 text-xs sm:text-sm font-semibold transition-colors min-h-[48px] rounded-xl"><ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" /><span>{t('optimizer.applyStep.direct')}</span></button>
                      <button type="button" onClick={handleUseSnippets} disabled={applying || (applicationMethod !== null && applicationMethod !== 'snippets')} style={{ ...(applicationMethod === 'snippets' ? { background: 'var(--vscode-primary)', color: 'var(--vscode-primary-text)' } : applicationMethod === null ? { background: 'var(--vscode-primary-bg)', color: 'var(--vscode-primary)' } : { background: 'var(--vscode-input-background)', color: 'var(--vscode-text-tertiary)', opacity: 0.6 }) }} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-3 text-xs sm:text-sm font-semibold transition-colors min-h-[48px] rounded-xl"><Clipboard className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" /><span>{snippetCopied ? '✓' : t('optimizer.applyStep.snippets')}</span></button>
                      <button type="button" onClick={handleApplyLater} disabled={applying || (applicationMethod !== null && applicationMethod !== 'later')} style={{ ...(applicationMethod === 'later' ? { background: 'var(--vscode-primary)', color: 'var(--vscode-primary-text)' } : applicationMethod === null ? { background: 'var(--vscode-primary-bg)', color: 'var(--vscode-primary)' } : { background: 'var(--vscode-input-background)', color: 'var(--vscode-text-tertiary)', opacity: 0.6 }) }} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-3 text-xs sm:text-sm font-semibold transition-colors min-h-[48px] rounded-xl"><Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" /><span>{t('optimizer.applyStep.later')}</span></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        </div>
        <nav className={STEP_NAV_CLASS} aria-label="Step navigation">
          <div className="w-full flex flex-row justify-between items-center gap-2 pt-1 sm:pt-0">
            <button type="button" onClick={backDisabled ? undefined : (applied && onExitToDashboard ? onExitToDashboard : onBack)} disabled={backDisabled} className={`flex items-center justify-center gap-2 min-h-[44px] px-3 sm:px-4 py-2.5 font-medium rounded-xl transition-colors text-sm border vscode-border touch-manipulation flex-shrink-0 ${backDisabled ? 'vscode-bg-tertiary vscode-text-tertiary cursor-not-allowed opacity-60 pointer-events-none' : 'vscode-bg-tertiary hover:vscode-bg-hover vscode-text-secondary hover:vscode-text-primary'}`}><ArrowLeft className="w-4 h-4 shrink-0" /><span className="hidden sm:inline">{applied && onExitToDashboard ? t('optimizer.applyStep.backToDashboard') : t('optimizer.applyStep.back')}</span></button>
            {!applied ? (
              <button type="button" onClick={handleSkip} disabled={applying || isLoading} className="flex items-center justify-center gap-1.5 min-h-[44px] px-3 sm:px-4 py-2.5 text-sm vscode-bg-tertiary hover:vscode-bg-hover vscode-text-secondary rounded-xl transition-colors disabled:opacity-50 border vscode-border touch-manipulation flex-shrink-0"><span>{t('optimizer.applyStep.skip')}</span></button>
            ) : (
              <span className="flex-shrink-0 min-w-[60px]" aria-hidden />
            )}
            <button type="button" onClick={handleNext} disabled={applying || isLoading} className={`flex items-center justify-center gap-1.5 min-h-[44px] px-3 sm:px-5 py-2.5 rounded-xl font-semibold text-sm transition-opacity border-2 touch-manipulation flex-shrink-0 ${!applying && !isLoading && applied ? primaryButtonClass + ' border-transparent' : 'vscode-bg-tertiary hover:vscode-bg-hover vscode-text-secondary hover:vscode-text-primary vscode-border cursor-pointer'}`}><span>{applied ? t('optimizer.nextActionModal.trackApplicationBtn') : t('optimizer.resultsStep.continue')}</span><ArrowRight className="w-4 h-4 shrink-0" /></button>
          </div>
        </nav>
      </div>
      )}
    </motion.div>
  );
};

export default React.memo(ApplyStep);
